/**
 * ContactsList — agenda de contactos de la empresa (estándar de listados).
 *
 * Cada contacto puede estar vinculado a cualquier elemento del CMDB
 * (servidores, VMs, servicios, cámaras…) con un papel: responsable, soporte,
 * escalado, proveedor o facturación. La ficha de cada elemento monta el mismo
 * panel (`ContactsPanel`), así que se puede llegar desde los dos lados.
 *
 * Columnas: papel/relación no (un contacto puede tener varios vínculos), pero sí
 * el nº de elementos. El clic en la fila abre la ficha del contacto con sus
 * vínculos.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  UserGroupIcon,
  PlusIcon,
  EnvelopeIcon,
  PhoneIcon,
  DevicePhoneMobileIcon,
  PencilSquareIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ListPage from './common/ListPage';
import Dialog from './common/Dialog';

const FORM_VACIO = {
  id: null,
  name: '',
  role: '',
  department: '',
  email: '',
  phone: '',
  mobile: '',
  company_name: '',
  notes: '',
  is_active: true,
};

export default function ContactsList({ companyId }) {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ object_types: [], relations: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');

  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [saving, setSaving] = useState(false);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.getContacts(companyId);
      setItems(res.data || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los contactos');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.getContactsMeta().then(setMeta).catch(() => {});
  }, []);

  const abrirDetalle = async (contacto) => {
    setOpenId(contacto.id);
    setDetailLoading(true);
    setEditando(false);
    try {
      const data = await api.getContact(contacto.id, companyId);
      setDetail(data);
      setForm({ ...FORM_VACIO, ...data });
    } catch (err) {
      setError(err.message || 'No se pudo abrir el contacto');
    } finally {
      setDetailLoading(false);
    }
  };

  const nuevo = () => {
    setForm(FORM_VACIO);
    setDetail(null);
    setOpenId('nuevo');
    setEditando(true);
  };

  const guardar = async () => {
    if (!form.name.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.company_id;
      delete payload.created_by;
      delete payload.links;
      delete payload.link_count;
      let guardado;
      if (form.id) guardado = await api.updateContact(form.id, payload, companyId);
      else guardado = await api.createContact(payload, companyId);
      await cargar();
      if (guardado?.id) {
        setOpenId(guardado.id);
        setDetail(guardado);
        setForm({ ...FORM_VACIO, ...guardado });
        setEditando(false);
      } else {
        setOpenId(null);
      }
    } catch (err) {
      setError(err.message || 'No se pudo guardar el contacto');
    } finally {
      setSaving(false);
    }
  };

  const moverRelacion = async (link, relation) => {
    try {
      await api.updateContactLink(link.contact_id, link.id, { relation }, companyId);
      const data = await api.getContact(link.contact_id, companyId);
      setDetail(data);
    } catch (err) {
      setError(err.message || 'No se pudo cambiar el papel');
    }
  };

  const quitarVinculo = async (link) => {
    try {
      await api.deleteContactLink(link.contact_id, link.id, companyId);
      const data = await api.getContact(link.contact_id, companyId);
      setDetail(data);
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo quitar el vínculo');
    }
  };

  const departments = useMemo(
    () => [...new Set(items.map((c) => (c.department || '').trim()).filter(Boolean))].sort(),
    [items],
  );

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((c) => {
      const coincide = !q
        || (c.name || '').toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q)
        || (c.role || '').toLowerCase().includes(q)
        || (c.department || '').toLowerCase().includes(q)
        || (c.company_name || '').toLowerCase().includes(q)
        || (c.phone || '').toLowerCase().includes(q);
      const estado = !filterStatus || (filterStatus === 'active' ? c.is_active : !c.is_active);
      const area = !filterDepartment || (c.department || '') === filterDepartment;
      return coincide && estado && area;
    });
  }, [items, search, filterStatus, filterDepartment]);

  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'name',
        label: 'Nombre',
        sortable: true,
        render: (c) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <UserGroupIcon style={{ width: 15, height: 15, color: 'var(--text-muted)' }} />
            <strong style={{ fontWeight: 600 }}>{c.name}</strong>
          </span>
        ),
      },
      { key: 'role', label: 'Puesto', sortable: true, render: (c) => c.role || dash },
      {
        key: 'department',
        label: 'Departamento',
        sortable: true,
        render: (c) => (c.department ? <span className="list-badge">{c.department}</span> : dash),
      },
      { key: 'company_name', label: 'Empresa', sortable: true, render: (c) => c.company_name || dash },
      {
        key: 'email',
        label: 'Correo',
        sortable: true,
        render: (c) => (c.email ? (
          <a href={`mailto:${c.email}`} onClick={(event) => event.stopPropagation()} style={{ color: 'var(--accent-l)' }}>
            {c.email}
          </a>
        ) : dash),
      },
      {
        key: 'phone',
        label: 'Teléfono',
        sortable: true,
        render: (c) => (
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            {c.phone && (
              <a href={`tel:${c.phone}`} onClick={(event) => event.stopPropagation()} title={c.phone}>
                <PhoneIcon style={{ width: 13, height: 13 }} />
              </a>
            )}
            {c.mobile && (
              <a href={`tel:${c.mobile}`} onClick={(event) => event.stopPropagation()} title={`Móvil: ${c.mobile}`}>
                <DevicePhoneMobileIcon style={{ width: 13, height: 13 }} />
              </a>
            )}
            {!c.phone && !c.mobile ? dash : <span className="list-code">{c.phone || c.mobile}</span>}
          </span>
        ),
      },
      {
        key: 'link_count',
        label: 'Elementos',
        sortable: true,
        render: (c) => (
          <span className={`list-badge ${c.link_count ? 'list-badge--accent' : 'list-badge--muted'}`}>
            {c.link_count || 0}
          </span>
        ),
      },
      {
        key: 'is_active',
        label: 'Estado',
        sortable: true,
        render: (c) => (
          <span className={`list-badge ${c.is_active ? 'list-badge--on' : 'list-badge--off'}`}>
            {c.is_active ? 'Activo' : 'Inactivo'}
          </span>
        ),
      },
    ];
  }, []);

  const exportRows = useMemo(
    () => filtrados.map((c) => ({
      Nombre: c.name,
      Puesto: c.role || '',
      Departamento: c.department || '',
      Empresa: c.company_name || '',
      Correo: c.email || '',
      Teléfono: c.phone || '',
      Móvil: c.mobile || '',
      Elementos: c.link_count || 0,
      Estado: c.is_active ? 'Activo' : 'Inactivo',
    })),
    [filtrados],
  );

  const contacto = detail || (openId ? items.find((c) => c.id === openId) : null);

  return (
    <>
      <ListPage
        icon={UserGroupIcon}
        title="Contactos"
        subtitle="A quién llamar: responsables, soporte, escalado y proveedores de cualquier elemento del CMDB."
        companyId={companyId}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nombre, correo, puesto, empresa…"
        filters={[
          {
            key: 'status',
            label: 'Estado',
            allLabel: 'Todos los estados',
            value: filterStatus,
            onChange: setFilterStatus,
            options: [
              { value: 'active', label: 'Activos' },
              { value: 'inactive', label: 'Inactivos' },
            ],
          },
          {
            key: 'department',
            label: 'Departamento',
            allLabel: 'Todos los departamentos',
            value: filterDepartment,
            onChange: setFilterDepartment,
            options: departments.map((d) => ({ value: d, label: d })),
          },
        ]}
        columns={columns}
        columnStorageKey="cmdb_cols_contacts"
        listKey="contacts"
        onRowClick={abrirDetalle}
        rows={filtrados}
        exportRows={exportRows}
        exportFileBase="contactos"
        loading={loading && items.length === 0}
        error={error}
        onCreate={nuevo}
        createLabel="Nuevo contacto"
        importEntity="contacts"
        onImported={cargar}
        empty={{
          icon: '👥',
          title: items.length === 0 ? 'Todavía no hay contactos' : 'Ningún contacto coincide',
          subtitle: items.length === 0
            ? 'Crea el primero y vincúlalo desde la ficha de cualquier servidor, VM o servicio.'
            : undefined,
          action: items.length === 0 ? (
            <button type="button" className="list-btn-primary" onClick={nuevo}>
              <PlusIcon style={{ width: 14, height: 14 }} /> Nuevo contacto
            </button>
          ) : undefined,
        }}
        actions={{
          onEdit: (row) => {
            setForm({ ...FORM_VACIO, ...row });
            setDetail(null);
            setOpenId(row.id);
            setEditando(true);
          },
          onDelete: async (row) => {
            await api.deleteContact(row.id, companyId);
            await cargar();
          },
          deleteTitle: 'Eliminar contacto',
          deleteMessage: (row) => `¿Eliminar el contacto «${row.name}»? Se quitan también sus vínculos con elementos (los elementos no se borran).`,
          onCopy: async (row) => {
            await api.cloneContact(row.id, companyId);
            await cargar();
          },
        }}
      />

      {(openId || editando) && (
        <Dialog
          title={editando ? (form.id ? `Editar ${form.name || 'contacto'}` : 'Nuevo contacto') : (contacto?.name || 'Contacto')}
          icon={<UserGroupIcon style={{ width: 18, height: 18 }} />}
          maxWidth={720}
          busy={saving || detailLoading}
          onClose={() => { setOpenId(null); setEditando(false); setDetail(null); }}
          footer={(
            <>
              {!editando && contacto && (
                <button
                  type="button"
                  className="list-btn-secondary"
                  style={{ marginRight: 'auto' }}
                  onClick={() => { setForm({ ...FORM_VACIO, ...contacto }); setEditando(true); }}
                >
                  <PencilSquareIcon style={{ width: 14, height: 14 }} /> Editar
                </button>
              )}
              {editando ? (
                <>
                  <button
                    type="button"
                    className="list-btn-secondary"
                    onClick={() => {
                      if (form.id) { setEditando(false); setForm({ ...FORM_VACIO, ...(detail || contacto || {}) }); }
                      else { setOpenId(null); setEditando(false); }
                    }}
                    disabled={saving}
                  >
                    {form.id ? 'Descartar' : 'Cancelar'}
                  </button>
                  <button type="button" className="list-btn-primary" onClick={guardar} disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar'}
                  </button>
                </>
              ) : (
                <button type="button" className="list-btn-secondary" onClick={() => { setOpenId(null); setDetail(null); }}>
                  Cerrar
                </button>
              )}
            </>
          )}
        >
          {editando ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
              <label className="list-hint" style={{ gridColumn: '1 / -1' }}>
                Nombre *
                <input className="list-input" style={{ width: '100%', marginTop: 3 }} value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
              </label>
              <label className="list-hint">
                Puesto
                <input className="list-input" style={{ width: '100%', marginTop: 3 }} value={form.role || ''}
                  onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))} />
              </label>
              <label className="list-hint">
                Departamento
                <input className="list-input" style={{ width: '100%', marginTop: 3 }} value={form.department || ''}
                  onChange={(e) => setForm((p) => ({ ...p, department: e.target.value }))} />
              </label>
              <label className="list-hint">
                Empresa
                <input className="list-input" style={{ width: '100%', marginTop: 3 }} value={form.company_name || ''}
                  onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))} />
              </label>
              <label className="list-hint">
                Correo
                <input className="list-input" style={{ width: '100%', marginTop: 3 }} value={form.email || ''}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
              </label>
              <label className="list-hint">
                Teléfono
                <input className="list-input" style={{ width: '100%', marginTop: 3 }} value={form.phone || ''}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
              </label>
              <label className="list-hint">
                Móvil
                <input className="list-input" style={{ width: '100%', marginTop: 3 }} value={form.mobile || ''}
                  onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))} />
              </label>
              <label className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 20 }}>
                <input type="checkbox" checked={Boolean(form.is_active)}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))} />
                Activo
              </label>
              <label className="list-hint" style={{ gridColumn: '1 / -1' }}>
                Notas
                <textarea className="list-input" rows={3} style={{ width: '100%', marginTop: 3 }} value={form.notes || ''}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
              </label>
            </div>
          ) : contacto && (
            <>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                {contacto.email && (
                  <a className="list-badge list-badge--accent" href={`mailto:${contacto.email}`}>
                    <EnvelopeIcon style={{ width: 13, height: 13 }} /> {contacto.email}
                  </a>
                )}
                {contacto.phone && (
                  <a className="list-badge" href={`tel:${contacto.phone}`}>
                    <PhoneIcon style={{ width: 13, height: 13 }} /> {contacto.phone}
                  </a>
                )}
                {contacto.mobile && (
                  <a className="list-badge" href={`tel:${contacto.mobile}`}>
                    <DevicePhoneMobileIcon style={{ width: 13, height: 13 }} /> {contacto.mobile}
                  </a>
                )}
                <span className={`list-badge ${contacto.is_active ? 'list-badge--on' : 'list-badge--off'}`}>
                  {contacto.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, margin: 0 }}>
                {[['Puesto', contacto.role], ['Departamento', contacto.department], ['Empresa', contacto.company_name]].map(([label, value]) => (
                  <div key={label}>
                    <dt className="list-hint" style={{ margin: 0 }}>{label}</dt>
                    <dd style={{ margin: 0, fontSize: '0.85rem' }}>{value || '—'}</dd>
                  </div>
                ))}
              </dl>

              {contacto.notes && (
                <p style={{ marginTop: 12, whiteSpace: 'pre-wrap', fontSize: '0.84rem', color: 'var(--text-secondary)' }}>
                  {contacto.notes}
                </p>
              )}

              <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <UserPlusIcon style={{ width: 15, height: 15 }} />
                  <strong style={{ fontSize: '0.84rem' }}>Elementos vinculados ({(detail?.links || []).length})</strong>
                </div>
                {(detail?.links || []).length === 0 && (
                  <p className="list-hint" style={{ margin: 0 }}>
                    Sin vínculos. Se añaden desde la ficha del servidor, VM, servicio… (panel «Contactos»).
                  </p>
                )}
                {(detail?.links || []).map((link) => (
                  <div key={link.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                    <span className="list-badge">{link.object_type_label}</span>
                    <strong style={{ fontSize: '0.82rem' }}>{link.object_name || `#${link.object_id}`}</strong>
                    <select
                      className="list-select"
                      style={{ marginLeft: 'auto', fontSize: '0.76rem', padding: '2px 6px' }}
                      value={link.relation}
                      onChange={(event) => moverRelacion(link, event.target.value)}
                    >
                      {meta.relations.map((relacion) => (
                        <option key={relacion.value} value={relacion.value}>{relacion.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="list-btn-secondary"
                      style={{ padding: '2px 7px', fontSize: '0.72rem' }}
                      onClick={() => quitarVinculo(link)}
                    >
                      Quitar
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </Dialog>
      )}
    </>
  );
}
